<script>
  import { onMount } from "svelte";
  import * as d3 from "d3";
  import * as topojson from "topojson-client";
  import { geoAlbersUsaTerritories } from "d3-composite-projections";

  export let districtsTopo;   // TopoJSON of courts
  export let statesTopo;      // TopoJSON of US states
  export let cities; // Some large cities in the U.S. plus Minneapolis
  export let data = [];   // [{ court_id: "001", date_filed: "2023-01-01" }, ...]
  export let type; // String, what type of map is it for the toggle
  export let mapping = []; // [{ state_abbreviation: "ca", "state_name": "California", ... }]
  export let courts_mapping = []; // [{ court_id: "akd", "court_name": "District of Alaska", ... }] Note for future refactoring, there might be a way to combine the two mapping JSONs
  export let wrapperID = "";

  // Convert TopoJSON to GeoJSON
  $: districts = districtsTopo ? topojson.feature(districtsTopo, districtsTopo.objects[Object.keys(districtsTopo.objects)[0]]) : null;
  $: states = statesTopo ? topojson.feature(statesTopo, statesTopo.objects[Object.keys(statesTopo.objects)[0]]) : null;

  let width;
  let height;

  let mobileBreakpoint = 664;

  let svgEl;
  let tooltipEl;
  let wrapperEl;

  function getWidth() {
    return wrapperEl.node().getBoundingClientRect().width;
  }

  function updateWidth() {
    if (!wrapperEl) return;
    width = getWidth();
    height = width * 0.55; // US map aspect ratio is roughly 1.9:1
    if (svgEl) render();
  }

  // --- DATA FUNCTIONS ---

  function groupByEntity(data, type) {
    const counts = d3.rollup(
      data,
      v => v.length,
      d => type == 'state' ? d.state : d.court_id
    );
    return counts; // Map {state => count} or Map {court_id => count} depending on type
  }

  function getStateName(mapping, property) {
    const map = new Map(mapping.map((o) => [o.state_name, o.state_abbreviation]));
    return map.get(property)
  }

  function getCourtName(mapping, property) {
    const map = new Map(mapping.map((o) => [o.court_id, o.court_name]));
    
    return map.get(property)
  }


  function joinData(data, type) {
    // Groups and then maps data depending on what type of map we're showing based on toggle

    let attachedData;

    const grouped = groupByEntity(data, type)


    if (type == 'state') {

      return states.features.map(f => ({
      ...f,
      count: grouped.get(getStateName(mapping, f.properties.NAME)) || 0
    }));

    }

    else {
    return districts.features.map(f => ({
      ...f,
      count: grouped.get(f.properties.court_id) || 0
    }));

    }


  }

  function render() {
    if (!districts || !states) return;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();
    const tooltip = d3.select(tooltipEl);
    // is the map mobile?
    const isMobile = width < mobileBreakpoint;

    // --- projection + path ---
    // Use AlbersUsaTerritories to include Puerto Rico and US Virgin Islands
    const projection = geoAlbersUsaTerritories()
      .fitExtent([[0, 0], [width, height]], states);
    const path = d3.geoPath().projection(projection);
    
 

    // --- join data ---
    const casesCounts = joinData(data, type);
    

    const maxCount = d3.max(casesCounts, d => d.count);
    const styles = getComputedStyle(document.body);
    const colorMin = styles.getPropertyValue('--map-light-color').trim();
    const colorMax = styles.getPropertyValue('--map-dark-color').trim();
    const color0 = styles.getPropertyValue('--map-0-color').trim();
    const color = d3.scaleSequential(d3.interpolateRgb(colorMin, colorMax))
      .domain([1, maxCount]);

    // --- draw districts (fill only, no stroke) ---
    svg.selectAll(`path.entity_${type}`)
      .data(casesCounts)
      .join("path")
      .attr("class", `entity_${type}`)
      .attr("d", path)
      .attr("fill", d => d.count == 0 ? color0 : color(d.count))
      .attr("stroke", "none") // remove court borders
      .on("mouseenter", (event, d) => {
        const name = type == 'state' ? d.properties.NAME : getCourtName(courts_mapping, d.properties.court_id)
        tooltip
          .style("opacity", 1)
          .html(
            `<div class='map_tooltip'><div class='entity_title'>${name}</div>
             ${d.count.toLocaleString('en-US')} case${d.count == 1 ? '' : 's'} since Jan. 2025</div>`
          );
      })
      .on("mousemove", (event) => {
        const tooltipNode = tooltip.node();
        const tooltipWidth = tooltipNode.offsetWidth;
        const tooltipHeight = tooltipNode.offsetHeight;
        const pageWidth = window.innerWidth;
        const pageHeight = window.innerHeight + window.scrollY;

        // Initial left position logic
        let left = event.pageX + tooltipWidth + 20 > pageWidth
        ? event.pageX - tooltipWidth - 10
        : event.pageX + 10;

        // If tooltip would go off the left edge, center it horizontally
        if (left < 0) {
        left = (pageWidth - tooltipWidth) / 2;
        }

        // Position above cursor if would go off bottom edge
        const top = event.pageY + tooltipHeight + 10 > pageHeight
        ? event.pageY - tooltipHeight - 10
        : event.pageY - 28;

        
        tooltip
          .style("left", left + "px")
          .style("top", top + "px");
      })
      .on("mouseleave", () => {
        tooltip.style("opacity", 0);
      });

    // --- draw US state borders ---
    svg.selectAll("path.state")
      .data(states.features)
      .join("path")
      .attr("class", "state")
      .attr("d", path)
      .attr("fill", "none")
      .attr("stroke", "var(--background-color)")
      .attr("stroke-width", isMobile ? 0.5 : 1);

  // Eliminate some cities in mobile
  const filteredCitiesMobile = cities.filter(d => {
    if (isMobile) return d.present_in_mobile == true;
    else return d;
    })


  // Add the circles for cities
   svg.selectAll("cities")
    .data(filteredCitiesMobile)
    .enter()
    .append("circle")
    .attr("class", "cities")
    .attr("cx", d => {
    return projection([d.longitude, d.latitude])[0];
  })
    .attr("cy", d => {
    return projection([d.longitude, d.latitude])[1];
  })
    .attr("r", isMobile ? 2 : 3);

    // Add the labels for cities
    svg.selectAll(".city-label")
     .data(filteredCitiesMobile)
    .enter()
    .append("text")
    .attr("class", "city-label")
    .attr("transform", d => {
    return "translate(" + projection([d.longitude, d.latitude]) + ")";
  })
    .attr("x", d => {
    if (isMobile) return 5 + d.pos_mobile
    else return 5 + d.pos_desktop;
    })
    .attr("y", d => {
    if (isMobile) return d.y_mobile
    else return 0;
    })
  .attr("dy", ".35em") // Vertical alignment
  .text(d => d.city); // Label text

  }

  onMount(() => {
    wrapperEl = d3.select(wrapperID);
    updateWidth();
    render();
    let resizeTimeout;
    const listener = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateWidth, 50);
    };
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  });
  $: districtsTopo && statesTopo && svgEl && render();
</script>

<svg bind:this={svgEl} {width} {height}></svg>
<div class="tooltip" bind:this={tooltipEl}></div>
